import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { UserCircle, Lock, Save } from 'lucide-react';

const Profile = () => {
  const { user } = useAuth();
  const roleColor = { admin: '#ef4444', manager: '#a855f7', employee: '#3b82f6', client: '#22c55e' };

  const [profileForm, setProfileForm] = useState({ name: user?.name || '' });
  const [profileMsg, setProfileMsg] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  const updateProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) return;
    setProfileLoading(true);
    setProfileMsg(null);
    try {
      await axios.put('/api/auth/profile', { name: profileForm.name });
      setProfileMsg({ type: 'success', text: 'Name updated successfully! Refresh to see changes in sidebar.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update' });
    } finally { setProfileLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setPwMsg({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    setPwLoading(true);
    setPwMsg(null);
    try {
      await axios.post('/api/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwMsg({ type: 'success', text: 'Password changed successfully!' });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.message || 'Failed to change password' });
    } finally { setPwLoading(false); }
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';

  return (
    <div className="page-content">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Profile</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Manage your account settings</p>
      </div>

      {/* Avatar card */}
      <div className="card" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, flexShrink: 0 }}>
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{user?.name}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{user?.email}</div>
          <div style={{ marginTop: 6 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: `${roleColor[user?.role]}22`, color: roleColor[user?.role] }}>
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      <div className="profile-grid">
        {/* Update Name */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <UserCircle size={18} style={{ color: 'var(--text-muted)' }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Personal Info</div>
          </div>
          <form onSubmit={updateProfile}>
            <div className="input-group">
              <label className="input-label">Full Name</label>
              <input className="input" required value={profileForm.name} onChange={e => setProfileForm({ name: e.target.value })} placeholder="Your name" />
            </div>
            <div className="input-group">
              <label className="input-label">Email</label>
              <input className="input" value={user?.email || ''} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Email cannot be changed</div>
            </div>
            {profileMsg && (
              <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, background: profileMsg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: profileMsg.type === 'success' ? 'var(--green)' : 'var(--red)', border: `1px solid ${profileMsg.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
                {profileMsg.text}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={profileLoading}>
              <Save size={15} /> {profileLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <Lock size={18} style={{ color: 'var(--text-muted)' }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Change Password</div>
          </div>
          <form onSubmit={changePassword}>
            <div className="input-group">
              <label className="input-label">Current Password</label>
              <input type="password" className="input" required value={pwForm.currentPassword} onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} placeholder="Current password" />
            </div>
            <div className="input-group">
              <label className="input-label">New Password</label>
              <input type="password" className="input" required value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="New password (min 6 chars)" />
            </div>
            <div className="input-group">
              <label className="input-label">Confirm New Password</label>
              <input type="password" className="input" required value={pwForm.confirmPassword} onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))} placeholder="Confirm new password" />
            </div>
            {pwMsg && (
              <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, background: pwMsg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: pwMsg.type === 'success' ? 'var(--green)' : 'var(--red)', border: `1px solid ${pwMsg.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
                {pwMsg.text}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={pwLoading}>
              <Lock size={15} /> {pwLoading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
